/**
 * Claims DTOs — API contracts for claim readiness, generation and the claims register (Phase 11).
 * Backend: billing/claimReadiness.js, billing/claimRegister.js, billing/billingConfig.js.
 */
import type { ServiceCode } from '../../constants/disciplineRegistry';

// ── Readiness (GET /billing/claims/visits-ready?patientId=) ──────────────────

export type BlockScope = 'visit' | 'patient' | 'agency';

/** Why a visit cannot be billed. `box` names the CMS-1500 box when the gap is a form field. */
export interface ReadinessBlock {
  code: string;
  message: string;
  scope: BlockScope;
  box?: string;
  missing?: string[];
  claimId?: string | null;
}

export type PayerType = 'MEDICAID' | 'MEDICARE' | 'COMMERCIAL';

export interface ClaimPayer {
  coverageId: string;
  payerType: PayerType;
  payerKey: string;
  payerName: string;
  coverageType: string;
  program?: string | null;
  mco?: string | null;
  planName?: string | null;
  groupNumber?: string | null;
}

export type AgingLevel = 'ok' | 'amber' | 'red';
export type MinutesSource = 'EVV' | 'PELORA';

export interface ReadinessVisit {
  visitId: string;
  patientId: string;
  visitDate: string | null;
  status: string;
  discipline: string | null;
  service: string;
  providerId: string | null;
  providerName: string | null;
  evvRequired: boolean;
  evvStatus: string | null;
  minutes: number | null;
  minutesSource: MinutesSource;
  units: number | null;
  ratePerUnit: number | null;
  charge: number | null;
  code: { hcpcs: string; modifiers: string[]; unitType: string; minutesPerUnit: number | null } | null;
  payer: ClaimPayer | null;
  authNumber: string | null;
  flags: Array<{ code: string; visitId: string }>;
  daysSinceService: number | null;
  agingLevel: AgingLevel;
  blocks: ReadinessBlock[];
  ready: boolean;
}

export interface PayerGroup {
  payer: ClaimPayer | null;
  visitIds: string[];
  readyCount: number;
  totalCharge: number;
}

export interface BillingReadiness {
  patientId: string;
  today: string;
  agingDays: { amber: number; red: number };
  /** EVV-required dates no HHAX reconciliation run covers (BIL-02 hard stop) */
  reconciliationGaps: string[];
  /** Dates with no active primary coverage (INS-13) */
  noPrimaryDates: string[];
  agencyBlocks: ReadinessBlock[];
  patientBlocks: ReadinessBlock[];
  payers: PayerGroup[];
  visits: ReadinessVisit[];
  readyCount: number;
}

// ── Claims register ──────────────────────────────────────────────────────────

export type ClaimStatus =
  | 'DRAFT'
  | 'GENERATED'
  | 'SUBMITTED'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'PAID'
  | 'DENIED'
  | 'VOID'
  | 'CORRECTED';

export type SubmissionMethod = 'PORTAL' | 'PAPER' | 'CLEARINGHOUSE' | 'OTHER';
export type SecondaryBilled = 'Y' | 'N' | 'NA';
export type FrequencyCode = '1' | '7' | '8';

export interface ClaimStatusHistoryEntry {
  status: ClaimStatus;
  changedAt: string;
  changedBy: string;
  date?: string;
  method?: SubmissionMethod;
  amount?: number;
  reason?: string;
  payerClaimNumber?: string;
  notes?: string | null;
  note?: string;
}

/** Phase 17 (REQ-CLM-03): part of a visit on a line (PDN shifts are split at midnight). */
export interface ClaimLinePortion {
  visitId: string;
  date: string;
  minutes: number;
  portion: 'FULL' | 'BEFORE_MIDNIGHT' | 'AFTER_MIDNIGHT' | 'FULL_DAY';
}

export interface ClaimLine {
  line: number;
  visitId: string;
  /** Every visit behind the line (PDN: all shifts of the calendar day). */
  visitIds?: string[];
  portions?: ClaimLinePortion[];
  dateOfService: string;
  procedureCode: string;
  modifiers: string[];
  /** UB-04 FL 42 */
  revenueCode?: string | null;
  units: number;
  charges: number;
  minutes: number | null;
  minutesSource: MinutesSource;
}

export type ClaimForm = 'UB04' | 'CMS1500';
export type ClaimPortal = 'TEXMEDCONNECT' | 'AVAILITY' | 'MCO_PORTAL' | 'PAPER' | 'OTHER';

/** The payer/service claim rule the claim was built with (Phase 17, REQ-CLM-01). */
export interface ClaimRuleSummary {
  form: ClaimForm;
  tob?: string | null;
  fixedTob?: boolean;
  revenueCode?: string | null;
  linePolicy?: 'PER_DAY' | 'PER_VISIT';
  portal?: ClaimPortal | null;
  submitTo?: 'TMHP' | 'MCO' | 'PAYER' | null;
  appealDays?: number;
  source?: string | null;
  origin?: string;
}

export interface Claim {
  claimId: string;
  tenantId: string;
  patientId: string;
  patientName?: string;
  mrn?: string;
  claimGroupId?: string;
  sequence?: number;
  sequenceCount?: number;
  version?: number;
  originalClaimId?: string | null;
  rootClaimId?: string;
  frequencyCode?: FrequencyCode;
  originalReference?: string | null;
  supersededBy?: string | null;
  visitIds: string[];
  lines?: ClaimLine[];
  payer?: ClaimPayer;
  payerType?: string;
  linkedAuthorizationId?: string | null;
  authNumber?: string | null;
  dosFrom?: string;
  dosTo?: string;
  totalAmount?: number;
  totalUnits?: number;
  totalServiceLines?: number;
  flags?: Array<{ code: string; visitId: string }>;
  overrideFlag?: boolean;
  status: ClaimStatus;
  statusHistory: ClaimStatusHistoryEntry[];
  submissionDate?: string | null;
  submissionMethod?: SubmissionMethod | null;
  payerClaimNumber?: string | null;
  paymentAmount?: number | null;
  paymentDate?: string | null;
  denialReason?: string;
  rejectionReason?: string;
  /** Phase 17 (REQ-CLM-09) */
  denialReasonCode?: string | null;
  remarkCodes?: string[];
  appealBy?: string | null;
  claimForm?: ClaimForm;
  formType?: ClaimForm;
  typeOfBill?: string | null;
  statementFrom?: string;
  statementThrough?: string;
  claimRule?: ClaimRuleSummary | null;
  voidReason?: string;
  secondaryBilled?: SecondaryBilled | null;
  secondaryNote?: string | null;
  hasDocument?: boolean;
  generatedAt: string;
  generatedBy?: string;
  updatedAt?: string;
}

export interface ClaimVisitSummary {
  visitId: string;
  visitDate: string | null;
  providerName: string | null;
  discipline: string | null;
  status: string;
  evvStatus: string | null;
  billingClaimId: string | null;
  evvOverride: { claimFlag: string | null; minutes?: number; at?: string } | null;
}

export interface ClaimVersion {
  claimId: string;
  version: number;
  frequencyCode: FrequencyCode;
  status: ClaimStatus;
  generatedAt: string;
  totalAmount?: number;
}

export interface ClaimDetail extends Claim {
  visitSummaries: ClaimVisitSummary[];
  paSummary: { authorizationId: string; authorizationNumber: string | null; status: string; startDate: string | null; expirationDate: string | null } | null;
  versions: ClaimVersion[];
}

export interface ClaimsListParams {
  patientId?: string;
  payer?: string;
  status?: string;
  from?: string;
  to?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface ClaimsListResponse {
  claims: Claim[];
  total: number;
  offset: number;
  limit: number;
  hasMore: boolean;
}

export interface GenerateClaimsResponse {
  claimGroupId: string;
  claims: Claim[];
}

export interface UpdateClaimStatusRequest {
  status?: ClaimStatus;
  submissionDate?: string;
  submissionMethod?: SubmissionMethod;
  acceptedDate?: string;
  paymentDate?: string;
  paymentAmount?: number;
  reason?: string;
  date?: string;
  /** Phase 17 (REQ-CLM-09): payer adjustment reason code and remark codes on a denial/rejection */
  denialReasonCode?: string;
  remarkCodes?: string[];
  payerClaimNumber?: string;
  secondaryBilled?: SecondaryBilled;
  secondaryNote?: string;
  notes?: string;
}

export interface CorrectClaimRequest {
  frequencyCode: '7' | '8';
  originalReference?: string;
  reason?: string;
}

export interface ClaimDownloadResponse {
  signedUrl: string;
  expiresIn: number;
  fileName: string;
}

// ── Billing configuration (GET/PUT /billing/config) ──────────────────────────

/** Phase 20A: visit services from the discipline registry */
export type BillingService = ServiceCode;

export interface ServiceCodeRow {
  id?: string;
  payerKey: string;
  service: BillingService;
  discipline: string;
  hcpcs: string;
  modifiers: string[];
  unitType: 'units' | 'visits';
  minutesPerUnit: number | null;
  ratePerUnit: number | null;
  requiresAuth: boolean;
  description?: string | null;
  /** Phase 17 (REQ-CLM-05): required with a rate */
  rateSource?: string | null;
  rateEffectiveFrom?: string | null;
}

/** Phase 17 (REQ-CLM-01): the agency's confirmed claim form for a payer + service ('*' = any). */
export interface ClaimRuleOverride {
  form: ClaimForm;
  tob?: string;
  revenueCode?: string | null;
  patientStatus?: string;
  fixedTob?: boolean;
  linePolicy?: 'PER_DAY' | 'PER_VISIT';
  maxLines?: number;
  portal?: ClaimPortal;
  submitTo?: 'TMHP' | 'MCO' | 'PAYER';
  appealDays?: number;
  source: string;
}

export interface PayerRule {
  renderingNpiMode: 'agency' | 'individual';
  box32Mode: 'blank' | 'patientHome';
  box33bBenefitCode: boolean;
  claimRules?: Partial<Record<BillingService | '*', ClaimRuleOverride>>;
}

// ── Keying worksheet (GET /billing/claims/{claimId}/worksheet, Phase 17 REQ-CLM-07) ──

export interface WorksheetField {
  key: string;
  label: string;
  value: string;
}

export interface WorksheetLine {
  line: number;
  revenueCode: string | null;
  procedure: string;
  serviceDate: string;
  units: number;
  charges: number;
  placeOfService: string | null;
  diagnosisPointer: string | null;
}

export interface ClaimWorksheet {
  claimId: string;
  status: ClaimStatus;
  claimForm: ClaimForm;
  submitTo: 'TMHP' | 'MCO' | 'PAYER' | null;
  portal: ClaimPortal | null;
  orderSource: string;
  ruleSource: string | null;
  sections: Array<{ title: string; fields: WorksheetField[]; lines?: WorksheetLine[] }>;
}

export interface BillingConfig {
  serviceCodes: ServiceCodeRow[];
  agingDays: { amber: number; red: number };
  payerRules: Record<string, PayerRule>;
  /** Phase 20E: suggested rows not yet configured (RT, MSW and ABA codes; never seeded) */
  suggestions?: ServiceCodeRow[];
  isDefault?: boolean;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

// ── Discharge readiness (GET /patientmr/{patientId}/billing/unbilled) ─────────

export interface UnbilledVisits {
  patientId: string;
  unbilledCount: number;
  oldestDateOfService: string | null;
  visits: Array<{ visitId: string; visitDate: string | null; status: string }>;
}

// ── Display configuration ────────────────────────────────────────────────────

export const CLAIM_STATUS_BADGE: Record<ClaimStatus, { label: string; className: string }> = {
  DRAFT:     { label: 'Draft',            className: 'bg-gray-100 text-gray-700' },
  GENERATED: { label: 'Generated',        className: 'bg-blue-100 text-blue-700' },
  SUBMITTED: { label: 'Submitted',        className: 'bg-yellow-100 text-yellow-800' },
  ACCEPTED:  { label: 'Accepted (payer)', className: 'bg-green-100 text-green-800' },
  REJECTED:  { label: 'Rejected',         className: 'bg-red-100 text-red-800' },
  PAID:      { label: 'Paid',             className: 'bg-emerald-100 text-emerald-800' },
  DENIED:    { label: 'Denied',           className: 'bg-red-200 text-red-900' },
  VOID:      { label: 'Void',             className: 'bg-gray-200 text-gray-700' },
  CORRECTED: { label: 'Corrected',        className: 'bg-purple-100 text-purple-800' },
};

/** Mirror of the backend `claim` state machine (stateMachine.js) for the status menu. */
export const ALLOWED_NEXT_STATUSES: Partial<Record<ClaimStatus, ClaimStatus[]>> = {
  DRAFT:     ['GENERATED'],
  GENERATED: ['SUBMITTED'],
  SUBMITTED: ['ACCEPTED', 'PAID', 'DENIED', 'REJECTED'],
  ACCEPTED:  ['PAID', 'DENIED', 'REJECTED'],
};

/** Only unsubmitted claims are voided (REQ-BIL-11). */
export const VOIDABLE_STATUSES: ClaimStatus[] = ['DRAFT', 'GENERATED'];

/** Correction frequency allowed from each status (box 22). */
export const CORRECTION_BY_STATUS: Partial<Record<ClaimStatus, Array<'7' | '8'>>> = {
  REJECTED: ['7'],
  DENIED:   ['7'],
  SUBMITTED: ['8'],
  ACCEPTED: ['8'],
  PAID:     ['8'],
};

export const SUBMISSION_METHOD_LABELS: Record<SubmissionMethod, string> = {
  PORTAL: 'Payer portal',
  PAPER: 'Paper',
  CLEARINGHOUSE: 'Clearinghouse',
  OTHER: 'Other',
};
